import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatPercent } from '../utils/format';
import {
  adminApi,
  SystemHealth,
  SchedulerRun,
  SchedulerInfo,
  UpcomingTrade,
  UserSummary,
  ErrorSummary,
  SystemLog,
  AccountHealth,
  ActivityFeedItem,
  AdminStrategy,
  StrategyUpdate,
  SkippedTrade,
  SkippedTradesSummary
} from '../services/api';

type TabType = 'health' | 'scheduler' | 'upcoming' | 'users' | 'logs' | 'accounts' | 'activity' | 'strategies' | 'skipped';

const Admin: React.FC = () => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('health');

  // Data states
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [schedulerRuns, setSchedulerRuns] = useState<SchedulerRun[]>([]);
  const [schedulerInfo, setSchedulerInfo] = useState<SchedulerInfo | null>(null);
  const [upcomingTrades, setUpcomingTrades] = useState<UpcomingTrade[]>([]);
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [errorSummary, setErrorSummary] = useState<ErrorSummary | null>(null);
  const [systemLogs, setSystemLogs] = useState<SystemLog[]>([]);
  const [accountsHealth, setAccountsHealth] = useState<AccountHealth[]>([]);
  const [activityFeed, setActivityFeed] = useState<ActivityFeedItem[]>([]);
  const [strategies, setStrategies] = useState<AdminStrategy[]>([]);
  const [skippedTrades, setSkippedTrades] = useState<SkippedTrade[]>([]);
  const [skippedSummary, setSkippedSummary] = useState<SkippedTradesSummary | null>(null);

  // Strategy editing states
  const [editingStrategyId, setEditingStrategyId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<{
    max_position_value: string;
    max_position_pct: string;
    max_portfolio_allocation_pct: string;
  }>({ max_position_value: '', max_position_pct: '', max_portfolio_allocation_pct: '' });
  const [savingStrategy, setSavingStrategy] = useState(false);
  const [strategyMessage, setStrategyMessage] = useState<{ id: number; type: 'success' | 'error'; text: string } | null>(null);

  // Filter states
  const [logLevelFilter, setLogLevelFilter] = useState<string>('');
  const [logCategoryFilter, setLogCategoryFilter] = useState<string>('');

  // UI states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [triggering, setTriggering] = useState<string | null>(null);
  const [triggerResult, setTriggerResult] = useState<{ type: string; result: any } | null>(null);
  const [togglingAutoTrading, setTogglingAutoTrading] = useState(false);

  useEffect(() => {
    if (!token) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, activeTab, logLevelFilter, logCategoryFilter]);

  const loadData = async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      if (activeTab === 'health') {
        const [healthData, errorsData, infoData] = await Promise.all([
          adminApi.getSystemHealth(token),
          adminApi.getErrorSummary(token, 7),
          adminApi.getSchedulerInfo(token)
        ]);
        setHealth(healthData);
        setErrorSummary(errorsData);
        setSchedulerInfo(infoData);
      } else if (activeTab === 'scheduler') {
        const [runsData, infoData] = await Promise.all([
          adminApi.getSchedulerRuns(token, undefined, undefined, 14),
          adminApi.getSchedulerInfo(token)
        ]);
        setSchedulerRuns(runsData);
        setSchedulerInfo(infoData);
      } else if (activeTab === 'upcoming') {
        const data = await adminApi.getUpcomingTrades(token, 7);
        setUpcomingTrades(data);
      } else if (activeTab === 'users') {
        const data = await adminApi.getUsers(token);
        setUsers(data);
      } else if (activeTab === 'logs') {
        const data = await adminApi.getSystemLogs(
          token,
          logLevelFilter || undefined,
          logCategoryFilter || undefined,
          7
        );
        setSystemLogs(data);
      } else if (activeTab === 'accounts') {
        const data = await adminApi.getAccountsHealth(token);
        setAccountsHealth(data);
      } else if (activeTab === 'activity') {
        const data = await adminApi.getActivityFeed(token, 7, 50);
        setActivityFeed(data);
      } else if (activeTab === 'strategies') {
        const data = await adminApi.getStrategies(token);
        setStrategies(data);
      } else if (activeTab === 'skipped') {
        const [tradesData, summaryData] = await Promise.all([
          adminApi.getSkippedTrades(token, 7),
          adminApi.getSkippedTradesSummary(token, 7)
        ]);
        setSkippedTrades(tradesData);
        setSkippedSummary(summaryData);
      }
    } catch (err: any) {
      console.error('Failed to load data:', err);
      if (err.response?.status === 403) {
        setError('Admin access required');
      } else {
        setError(err.response?.data?.detail || 'Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerJob = async (jobType: string) => {
    if (!token) return;

    const confirmMsg = jobType === 'dividend_refresh'
      ? 'This will refresh dividend data from all sources. Continue?'
      : 'This will execute trades for all automated accounts. Continue?';

    if (!window.confirm(confirmMsg)) return;

    setTriggering(jobType);
    setTriggerResult(null);

    try {
      const result = await adminApi.triggerJob(jobType, token);
      setTriggerResult({ type: jobType, result: result.result });
      // Reload data
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to trigger job');
    } finally {
      setTriggering(null);
    }
  };

  const handleToggleAutoTrading = async () => {
    if (!token || !health) return;

    const newValue = !health.auto_trading_enabled;
    const confirmMsg = newValue
      ? 'Enable automatic trade execution? The system will execute trades automatically at scheduled times.'
      : 'Disable automatic trade execution? Scheduled trades will not execute until re-enabled.';

    if (!window.confirm(confirmMsg)) return;

    setTogglingAutoTrading(true);
    setError(null);

    try {
      await adminApi.updateSetting('auto_trading_enabled', newValue, token);
      // Update local health state to reflect the change
      setHealth({ ...health, auto_trading_enabled: newValue });
      // Reload scheduler info to reflect the change
      const infoData = await adminApi.getSchedulerInfo(token);
      setSchedulerInfo(infoData);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update auto-trading setting');
    } finally {
      setTogglingAutoTrading(false);
    }
  };

  const handleEditStrategy = (strategy: AdminStrategy) => {
    const params = strategy.parameters || {};
    setEditingStrategyId(strategy.id);
    setEditForm({
      max_position_value: params.max_position_value?.toString() || '',
      max_position_pct: params.max_position_pct?.toString() || '',
      max_portfolio_allocation_pct: params.max_portfolio_allocation_pct?.toString() || '',
    });
    setStrategyMessage(null);
  };

  const handleCancelEdit = () => {
    setEditingStrategyId(null);
    setStrategyMessage(null);
  };

  const handleSaveStrategy = async (strategy: AdminStrategy) => {
    if (!token) return;
    setSavingStrategy(true);
    setStrategyMessage(null);

    try {
      const updatedParams = { ...strategy.parameters };
      if (editForm.max_position_value !== '') {
        updatedParams.max_position_value = parseFloat(editForm.max_position_value);
      }
      if (editForm.max_position_pct !== '') {
        updatedParams.max_position_pct = parseFloat(editForm.max_position_pct);
      }
      if (editForm.max_portfolio_allocation_pct !== '') {
        updatedParams.max_portfolio_allocation_pct = parseFloat(editForm.max_portfolio_allocation_pct);
      }

      const update: StrategyUpdate = { parameters: updatedParams };
      const updated = await adminApi.updateStrategy(strategy.id, update, token);
      setStrategies(prev => prev.map(s => s.id === updated.id ? updated : s));
      setEditingStrategyId(null);
      setStrategyMessage({ id: strategy.id, type: 'success', text: 'Parameters saved' });
      setTimeout(() => setStrategyMessage(null), 3000);
    } catch (err: any) {
      setStrategyMessage({
        id: strategy.id,
        type: 'error',
        text: err.response?.data?.detail || 'Failed to save'
      });
    } finally {
      setSavingStrategy(false);
    }
  };

  const handleToggleStrategyField = async (strategy: AdminStrategy, field: 'is_active' | 'is_automated') => {
    if (!token) return;
    try {
      const update: StrategyUpdate = { [field]: !strategy[field] };
      const updated = await adminApi.updateStrategy(strategy.id, update, token);
      setStrategies(prev => prev.map(s => s.id === updated.id ? updated : s));
    } catch (err: any) {
      setStrategyMessage({
        id: strategy.id,
        type: 'error',
        text: err.response?.data?.detail || `Failed to toggle ${field}`
      });
    }
  };

  const handleToggleAccountActive = async (accountId: number) => {
    if (!token) return;
    try {
      const result = await adminApi.toggleAccountActive(accountId, token);
      setAccountsHealth(prev => prev.map(a =>
        a.account_id === accountId
          ? { ...a, is_active: result.is_active, connection_status: result.is_active ? a.connection_status : 'inactive' }
          : a
      ));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to toggle account');
    }
  };

  const formatDateTime = (dateStr: string | undefined | null): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const formatDate = (dateStr: string | undefined | null): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };


  const getStatusColor = (status: string): { bg: string; text: string } => {
    switch (status) {
      case 'success':
        return { bg: '#dcfce7', text: '#166534' };
      case 'failed':
        return { bg: '#fee2e2', text: '#991b1b' };
      case 'partial':
        return { bg: '#fef3c7', text: '#92400e' };
      case 'running':
        return { bg: '#dbeafe', text: '#1e40af' };
      default:
        return { bg: '#f3f4f6', text: '#374151' };
    }
  };

  // Check if user is admin
  if (user?.role !== 'admin') {
    return (
      <Layout>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px'
        }}>
          <div style={{
            textAlign: 'center',
            padding: '40px'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '24px' }}>🔒</div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#1e293b',
              marginBottom: '16px'
            }}>
              Admin Access Required
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#6b7280'
            }}>
              You don't have permission to access this page.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px'
      }}>
        {/* Header */}
        <div>
          <h2 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#1e293b',
            margin: '0 0 8px 0'
          }}>
            Admin Dashboard
          </h2>
          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            margin: 0
          }}>
            System monitoring and operational controls
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            backgroundColor: '#fee2e2',
            border: '1px solid #ef4444',
            borderRadius: '6px',
            padding: '12px 16px',
            color: '#b91c1c'
          }}>
            {error}
          </div>
        )}

        {/* Trigger Result */}
        {triggerResult && (
          <div style={{
            backgroundColor: '#dcfce7',
            border: '1px solid #10b981',
            borderRadius: '6px',
            padding: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'start'
          }}>
            <div>
              <div style={{ fontWeight: '600', color: '#166534', marginBottom: '8px' }}>
                Job Completed: {triggerResult.type.replace('_', ' ')}
              </div>
              <pre style={{
                fontSize: '13px',
                color: '#374151',
                margin: 0,
                whiteSpace: 'pre-wrap'
              }}>
                {JSON.stringify(triggerResult.result, null, 2)}
              </pre>
            </div>
            <button
              onClick={() => setTriggerResult(null)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                color: '#6b7280'
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '0',
          borderBottom: '2px solid #e5e7eb',
          overflowX: 'auto'
        }}>
          {[
            { id: 'health' as TabType, label: 'Health' },
            { id: 'activity' as TabType, label: 'Activity' },
            { id: 'skipped' as TabType, label: 'Skipped' },
            { id: 'accounts' as TabType, label: 'Accounts' },
            { id: 'strategies' as TabType, label: 'Strategies' },
            { id: 'logs' as TabType, label: 'Logs' },
            { id: 'scheduler' as TabType, label: 'Scheduler' },
            { id: 'upcoming' as TabType, label: 'Upcoming' },
            { id: 'users' as TabType, label: 'Users' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '12px 24px',
                border: 'none',
                backgroundColor: 'transparent',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                color: activeTab === tab.id ? '#3b82f6' : '#6b7280',
                borderBottom: activeTab === tab.id ? '2px solid #3b82f6' : '2px solid transparent',
                marginBottom: '-2px'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '300px'
          }}>
            <div style={{ fontSize: '16px', color: '#6b7280' }}>Loading...</div>
          </div>
        ) : (
          <>
            {/* Health Tab */}
            {activeTab === 'health' && health && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '24px'
              }}>
                {/* System Status */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  padding: '24px'
                }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1e293b',
                    margin: '0 0 20px 0'
                  }}>
                    System Status
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#6b7280' }}>Scheduler</span>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '13px',
                        fontWeight: '500',
                        backgroundColor: health.scheduler_status === 'running' ? '#dcfce7' : '#fee2e2',
                        color: health.scheduler_status === 'running' ? '#166534' : '#991b1b'
                      }}>
                        {health.scheduler_status}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#6b7280' }}>Auto-Trading</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{
                          fontSize: '13px',
                          fontWeight: '500',
                          color: health.auto_trading_enabled ? '#166534' : '#92400e'
                        }}>
                          {health.auto_trading_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                        <button
                          onClick={handleToggleAutoTrading}
                          disabled={togglingAutoTrading}
                          style={{
                            position: 'relative',
                            width: '48px',
                            height: '26px',
                            borderRadius: '13px',
                            border: 'none',
                            backgroundColor: health.auto_trading_enabled ? '#10b981' : '#d1d5db',
                            cursor: togglingAutoTrading ? 'wait' : 'pointer',
                            transition: 'background-color 0.2s',
                            opacity: togglingAutoTrading ? 0.6 : 1
                          }}
                          title={health.auto_trading_enabled ? 'Click to disable auto-trading' : 'Click to enable auto-trading'}
                        >
                          <span style={{
                            position: 'absolute',
                            top: '3px',
                            left: health.auto_trading_enabled ? '25px' : '3px',
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            backgroundColor: 'white',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                            transition: 'left 0.2s'
                          }} />
                        </button>
                      </div>
                    </div>
                    <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ color: '#6b7280', fontSize: '14px' }}>Last Dividend Refresh</span>
                        <span style={{ color: '#374151', fontSize: '14px' }}>
                          {formatDateTime(health.last_dividend_refresh)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#6b7280', fontSize: '14px' }}>Last Strategy Execution</span>
                        <span style={{ color: '#374151', fontSize: '14px' }}>
                          {formatDateTime(health.last_strategy_execution)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  padding: '24px'
                }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1e293b',
                    margin: '0 0 20px 0'
                  }}>
                    Platform Stats
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '16px'
                  }}>
                    <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b' }}>{health.total_users}</div>
                      <div style={{ fontSize: '13px', color: '#6b7280' }}>Users</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b' }}>{health.active_accounts}</div>
                      <div style={{ fontSize: '13px', color: '#6b7280' }}>Active Accounts</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#f59e0b' }}>{health.open_positions}</div>
                      <div style={{ fontSize: '13px', color: '#6b7280' }}>Open Positions</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#6b7280' }}>{health.pending_orders}</div>
                      <div style={{ fontSize: '13px', color: '#6b7280' }}>Pending Orders</div>
                    </div>
                  </div>
                </div>

                {/* Dividend Data */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  padding: '24px'
                }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1e293b',
                    margin: '0 0 20px 0'
                  }}>
                    Dividend Data
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280' }}>Total Events</span>
                      <span style={{ fontWeight: '600', color: '#1e293b' }}>{health.total_dividend_events}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280' }}>Upcoming Events</span>
                      <span style={{ fontWeight: '600', color: '#10b981' }}>{health.upcoming_dividend_events}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280' }}>Data Freshness</span>
                      <span style={{ fontWeight: '500', color: '#6b7280', fontSize: '14px' }}>
                        {formatDateTime(health.dividend_data_freshness)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Errors Summary */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  padding: '24px'
                }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1e293b',
                    margin: '0 0 20px 0'
                  }}>
                    Errors (Last 7 Days)
                  </h3>
                  {errorSummary && errorSummary.total_errors > 0 ? (
                    <div>
                      <div style={{
                        fontSize: '32px',
                        fontWeight: '700',
                        color: '#ef4444',
                        marginBottom: '16px'
                      }}>
                        {errorSummary.total_errors}
                      </div>
                      {Object.entries(errorSummary.by_category).map(([category, count]) => (
                        <div key={category} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '8px 0',
                          borderBottom: '1px solid #f3f4f6'
                        }}>
                          <span style={{ color: '#6b7280', textTransform: 'capitalize' }}>{category}</span>
                          <span style={{ fontWeight: '500', color: '#ef4444' }}>{count}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', color: '#10b981' }}>
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>✓</div>
                      <div>No errors</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Scheduler Tab */}
            {activeTab === 'scheduler' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Manual Triggers */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  padding: '24px'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#1e293b',
                    margin: '0 0 16px 0'
                  }}>
                    Manual Controls
                  </h3>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => handleTriggerJob('dividend_refresh')}
                      disabled={triggering !== null}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: triggering === 'dividend_refresh' ? '#9ca3af' : '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: triggering ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {triggering === 'dividend_refresh' ? 'Running...' : 'Refresh Dividend Data'}
                    </button>
                    <button
                      onClick={() => handleTriggerJob('strategy_execution')}
                      disabled={triggering !== null}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: triggering === 'strategy_execution' ? '#9ca3af' : '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: triggering ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {triggering === 'strategy_execution' ? 'Running...' : 'Execute Strategy Now'}
                    </button>
                  </div>
                </div>

                {/* Scheduled Jobs */}
                {schedulerInfo && (
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    padding: '24px'
                  }}>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: '#1e293b',
                      margin: '0 0 16px 0'
                    }}>
                      Scheduled Jobs
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {schedulerInfo.jobs.map((job, i) => (
                        <div key={i} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px 16px',
                          backgroundColor: '#f9fafb',
                          borderRadius: '6px'
                        }}>
                          <div>
                            <div style={{ fontWeight: '600', color: '#1e293b' }}>{job.name}</div>
                            <div style={{ fontSize: '13px', color: '#6b7280' }}>{job.schedule}</div>
                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                              {job.name.includes('Dividend') && 'Fetches upcoming dividend data from Alpaca and Yahoo Finance and stores it in the database.'}
                              {job.name.includes('Strategy') && 'Buys stocks the day before ex-dividend date and sells open positions on/after ex-date.'}
                              {job.name.includes('Fill') && 'Syncs fill prices from Alpaca for pending orders and updates position exit prices.'}
                            </div>
                          </div>
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: '500',
                            backgroundColor: job.enabled ? '#dcfce7' : '#fee2e2',
                            color: job.enabled ? '#166534' : '#991b1b'
                          }}>
                            {job.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Run History */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  overflow: 'hidden'
                }}>
                  <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: '#1e293b',
                      margin: 0
                    }}>
                      Run History (Last 14 Days)
                    </h3>
                  </div>
                  {schedulerRuns.length === 0 ? (
                    <div style={{
                      padding: '40px',
                      textAlign: 'center',
                      color: '#9ca3af'
                    }}>
                      No scheduler runs recorded yet
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f9fafb' }}>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Job</th>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Started</th>
                            <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Duration</th>
                            <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Status</th>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Result</th>
                          </tr>
                        </thead>
                        <tbody>
                          {schedulerRuns.map((run) => {
                            const statusColor = getStatusColor(run.status);
                            return (
                              <tr key={run.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                <td style={{ padding: '12px 16px' }}>
                                  <div style={{ fontWeight: '600', color: '#1e293b' }}>
                                    {run.job_type.replace('_', ' ')}
                                  </div>
                                  {run.job_name && (
                                    <div style={{ fontSize: '12px', color: '#6b7280' }}>{run.job_name}</div>
                                  )}
                                </td>
                                <td style={{ padding: '12px 16px', color: '#6b7280' }}>
                                  {formatDateTime(run.started_at)}
                                </td>
                                <td style={{ padding: '12px 16px', textAlign: 'right', color: '#374151' }}>
                                  {run.duration_seconds ? `${run.duration_seconds.toFixed(1)}s` : '-'}
                                </td>
                                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                  <span style={{
                                    padding: '4px 12px',
                                    borderRadius: '20px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    backgroundColor: statusColor.bg,
                                    color: statusColor.text,
                                    textTransform: 'capitalize'
                                  }}>
                                    {run.status}
                                  </span>
                                </td>
                                <td style={{ padding: '12px 16px', color: '#6b7280', fontSize: '13px' }}>
                                  {run.error_message ? (
                                    <span style={{ color: '#ef4444' }}>{run.error_message}</span>
                                  ) : run.result_summary ? (
                                    Object.entries(run.result_summary).map(([k, v]) => (
                                      <span key={k} style={{ marginRight: '12px' }}>
                                        {k.replace(/_/g, ' ')}: <strong>{String(v)}</strong>
                                      </span>
                                    ))
                                  ) : '-'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Upcoming Trades Tab */}
            {activeTab === 'upcoming' && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                overflow: 'hidden'
              }}>
                <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#1e293b',
                    margin: 0
                  }}>
                    Upcoming Trades (Next 7 Days)
                  </h3>
                </div>
                {upcomingTrades.length === 0 ? (
                  <div style={{
                    padding: '60px 24px',
                    textAlign: 'center',
                    color: '#9ca3af'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>📅</div>
                    <p style={{ fontSize: '16px', marginBottom: '8px' }}>No upcoming trades</p>
                    <p style={{ fontSize: '14px' }}>No dividend events scheduled in the next 7 days</p>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f9fafb' }}>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Trade Date</th>
                          <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Action</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Symbol</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Ex-Date</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Dividend</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Accounts</th>
                        </tr>
                      </thead>
                      <tbody>
                        {upcomingTrades.map((trade, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '12px 16px', fontWeight: '600', color: '#1e293b' }}>
                              {formatDate(trade.trade_date)}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              <span style={{
                                padding: '4px 12px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: '500',
                                backgroundColor: trade.action === 'buy' ? '#dcfce7' : '#fee2e2',
                                color: trade.action === 'buy' ? '#166534' : '#991b1b',
                                textTransform: 'uppercase'
                              }}>
                                {trade.action}
                              </span>
                            </td>
                            <td style={{ padding: '12px 16px', fontWeight: '600', color: '#1e293b' }}>
                              {trade.symbol}
                            </td>
                            <td style={{ padding: '12px 16px', color: '#6b7280' }}>
                              {formatDate(trade.ex_date)}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', color: '#10b981', fontWeight: '500' }}>
                              {formatCurrency(trade.dividend_amount)}
                            </td>
                            <td style={{ padding: '12px 16px', color: '#6b7280', fontSize: '13px' }}>
                              {trade.accounts.join(', ')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                overflow: 'hidden'
              }}>
                <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#1e293b',
                    margin: 0
                  }}>
                    All Users
                  </h3>
                </div>
                {users.length === 0 ? (
                  <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    color: '#9ca3af'
                  }}>
                    No users found
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f9fafb' }}>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>User</th>
                          <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Role</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Accounts</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Trades</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Total P&L</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Last Login</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u) => (
                          <tr key={u.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '12px 16px' }}>
                              <div style={{ fontWeight: '600', color: '#1e293b' }}>{u.username}</div>
                              <div style={{ fontSize: '12px', color: '#6b7280' }}>{u.email}</div>
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              <span style={{
                                padding: '4px 12px',
                                borderRadius: '20px',
                                fontSize: '12px',
                                fontWeight: '500',
                                backgroundColor: u.role === 'admin' ? '#dbeafe' : '#f3f4f6',
                                color: u.role === 'admin' ? '#1e40af' : '#374151',
                                textTransform: 'capitalize'
                              }}>
                                {u.role}
                              </span>
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', color: '#374151' }}>
                              {u.accounts_count}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', color: '#374151' }}>
                              {u.total_trades}
                            </td>
                            <td style={{
                              padding: '12px 16px',
                              textAlign: 'right',
                              fontWeight: '500',
                              color: u.total_pnl >= 0 ? '#10b981' : '#ef4444'
                            }}>
                              {formatCurrency(u.total_pnl)}
                            </td>
                            <td style={{ padding: '12px 16px', color: '#6b7280' }}>
                              {formatDateTime(u.last_login)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Logs Tab */}
            {activeTab === 'logs' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Filters */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  padding: '16px 24px',
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'center',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label style={{ fontSize: '14px', color: '#6b7280' }}>Level:</label>
                    <select
                      value={logLevelFilter}
                      onChange={(e) => setLogLevelFilter(e.target.value)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        fontSize: '14px'
                      }}
                    >
                      <option value="">All Levels</option>
                      <option value="debug">Debug</option>
                      <option value="info">Info</option>
                      <option value="warning">Warning</option>
                      <option value="error">Error</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label style={{ fontSize: '14px', color: '#6b7280' }}>Category:</label>
                    <select
                      value={logCategoryFilter}
                      onChange={(e) => setLogCategoryFilter(e.target.value)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        fontSize: '14px'
                      }}
                    >
                      <option value="">All Categories</option>
                      <option value="trading">Trading</option>
                      <option value="scheduler">Scheduler</option>
                      <option value="api">API</option>
                      <option value="auth">Auth</option>
                      <option value="broker">Broker</option>
                    </select>
                  </div>
                  <button
                    onClick={() => { setLogLevelFilter(''); setLogCategoryFilter(''); }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#f3f4f6',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      color: '#374151'
                    }}
                  >
                    Clear Filters
                  </button>
                </div>

                {/* Logs Table */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  overflow: 'hidden'
                }}>
                  <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: '#1e293b',
                      margin: 0
                    }}>
                      System Logs (Last 7 Days)
                    </h3>
                  </div>
                  {systemLogs.length === 0 ? (
                    <div style={{
                      padding: '60px 24px',
                      textAlign: 'center',
                      color: '#9ca3af'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                      <p style={{ fontSize: '16px' }}>No logs found</p>
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f9fafb' }}>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Time</th>
                            <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Level</th>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Category</th>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Message</th>
                          </tr>
                        </thead>
                        <tbody>
                          {systemLogs.map((log) => {
                            const levelColors: Record<string, { bg: string; text: string }> = {
                              debug: { bg: '#f3f4f6', text: '#6b7280' },
                              info: { bg: '#dbeafe', text: '#1e40af' },
                              warning: { bg: '#fef3c7', text: '#92400e' },
                              error: { bg: '#fee2e2', text: '#991b1b' },
                              critical: { bg: '#fecaca', text: '#7f1d1d' }
                            };
                            const colors = levelColors[log.level] || levelColors.info;
                            return (
                              <tr key={log.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                <td style={{ padding: '12px 16px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                                  {formatDateTime(log.created_at)}
                                </td>
                                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                  <span style={{
                                    padding: '4px 10px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    backgroundColor: colors.bg,
                                    color: colors.text,
                                    textTransform: 'uppercase'
                                  }}>
                                    {log.level}
                                  </span>
                                </td>
                                <td style={{ padding: '12px 16px', color: '#374151', fontWeight: '500' }}>
                                  {log.category}
                                </td>
                                <td style={{ padding: '12px 16px', color: '#1e293b', maxWidth: '400px' }}>
                                  <div style={{ wordBreak: 'break-word' }}>{log.message}</div>
                                  {log.error_type && (
                                    <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                                      {log.error_type}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Account Health Tab */}
            {activeTab === 'accounts' && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                gap: '20px'
              }}>
                {accountsHealth.length === 0 ? (
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    padding: '60px 24px',
                    textAlign: 'center',
                    color: '#9ca3af',
                    gridColumn: '1 / -1'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏦</div>
                    <p style={{ fontSize: '16px' }}>No trading accounts configured</p>
                  </div>
                ) : (
                  accountsHealth.map((account) => {
                    const statusColors: Record<string, { bg: string; text: string; border: string }> = {
                      connected: { bg: '#dcfce7', text: '#166534', border: '#10b981' },
                      error: { bg: '#fee2e2', text: '#991b1b', border: '#ef4444' },
                      inactive: { bg: '#f3f4f6', text: '#6b7280', border: '#9ca3af' },
                      unknown: { bg: '#fef3c7', text: '#92400e', border: '#f59e0b' }
                    };
                    const colors = statusColors[account.connection_status] || statusColors.unknown;

                    return (
                      <div
                        key={account.account_id}
                        style={{
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                          border: `2px solid ${colors.border}`,
                          overflow: 'hidden'
                        }}
                      >
                        {/* Header */}
                        <div style={{
                          padding: '16px 20px',
                          borderBottom: '1px solid #e5e7eb',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div>
                            <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                              {account.account_name}
                            </h4>
                            <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                              {account.broker.toUpperCase()} - {account.account_type}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{
                              padding: '6px 12px',
                              borderRadius: '20px',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: colors.bg,
                              color: colors.text,
                              textTransform: 'capitalize'
                            }}>
                              {account.connection_status}
                            </span>
                            <button
                              onClick={() => handleToggleAccountActive(account.account_id)}
                              style={{
                                position: 'relative',
                                width: '40px',
                                height: '22px',
                                borderRadius: '11px',
                                border: 'none',
                                backgroundColor: account.is_active ? '#10b981' : '#d1d5db',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s'
                              }}
                              title={account.is_active ? 'Active - click to disable trading' : 'Disabled - click to enable trading'}
                            >
                              <span style={{
                                position: 'absolute',
                                top: '2px',
                                left: account.is_active ? '20px' : '2px',
                                width: '18px',
                                height: '18px',
                                borderRadius: '50%',
                                backgroundColor: 'white',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                transition: 'left 0.2s'
                              }} />
                            </button>
                          </div>
                        </div>

                        {/* Content */}
                        <div style={{ padding: '16px 20px' }}>
                          {account.connection_error && (
                            <div style={{
                              backgroundColor: '#fee2e2',
                              padding: '10px 12px',
                              borderRadius: '6px',
                              marginBottom: '16px',
                              fontSize: '13px',
                              color: '#991b1b'
                            }}>
                              {account.connection_error}
                            </div>
                          )}

                          {account.connection_status === 'connected' && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                              <div style={{ backgroundColor: '#f9fafb', padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                                <div style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>
                                  {formatCurrency(account.buying_power || 0)}
                                </div>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>Buying Power</div>
                              </div>
                              <div style={{ backgroundColor: '#f9fafb', padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                                <div style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>
                                  {formatCurrency(account.portfolio_value || 0)}
                                </div>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>Portfolio Value</div>
                              </div>
                            </div>
                          )}

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                              <span style={{ color: '#6b7280' }}>Open Positions</span>
                              <span style={{ fontWeight: '500', color: '#1e293b' }}>{account.open_positions}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                              <span style={{ color: '#6b7280' }}>Pending Orders</span>
                              <span style={{ fontWeight: '500', color: '#1e293b' }}>{account.pending_orders}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                              <span style={{ color: '#6b7280' }}>Last Trade</span>
                              <span style={{ fontWeight: '500', color: '#6b7280' }}>
                                {account.last_trade_date ? formatDate(account.last_trade_date) : 'None'}
                              </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                              <span style={{ color: '#6b7280' }}>Recent Errors (7d)</span>
                              <span style={{
                                fontWeight: '500',
                                color: account.recent_errors > 0 ? '#ef4444' : '#10b981'
                              }}>
                                {account.recent_errors}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Activity Feed Tab */}
            {activeTab === 'activity' && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                overflow: 'hidden'
              }}>
                <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#1e293b',
                    margin: 0
                  }}>
                    Activity Feed (Last 7 Days)
                  </h3>
                </div>
                {activityFeed.length === 0 ? (
                  <div style={{
                    padding: '60px 24px',
                    textAlign: 'center',
                    color: '#9ca3af'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
                    <p style={{ fontSize: '16px' }}>No recent activity</p>
                  </div>
                ) : (
                  <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    {activityFeed.map((item) => {
                      const typeStyles: Record<string, { icon: string; bg: string; color: string }> = {
                        order: { icon: '📈', bg: '#dbeafe', color: '#1e40af' },
                        scheduler: { icon: '⏰', bg: '#f3e8ff', color: '#7c3aed' },
                        error: { icon: '❌', bg: '#fee2e2', color: '#991b1b' },
                        warning: { icon: '⚠️', bg: '#fef3c7', color: '#92400e' },
                        position: { icon: '💼', bg: '#dcfce7', color: '#166534' }
                      };
                      const style = typeStyles[item.event_type] || typeStyles.order;

                      return (
                        <div
                          key={item.id}
                          style={{
                            padding: '16px 24px',
                            borderBottom: '1px solid #f3f4f6',
                            display: 'flex',
                            gap: '16px',
                            alignItems: 'flex-start'
                          }}
                        >
                          {/* Icon */}
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: style.bg,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '18px',
                            flexShrink: 0
                          }}>
                            {style.icon}
                          </div>

                          {/* Content */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                              <div>
                                <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>
                                  {item.title}
                                </div>
                                <div style={{ color: '#6b7280', fontSize: '13px', marginTop: '4px' }}>
                                  {item.description}
                                </div>
                              </div>
                              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                                  {formatDateTime(item.timestamp)}
                                </div>
                                {item.status && (
                                  <span style={{
                                    display: 'inline-block',
                                    marginTop: '4px',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontWeight: '500',
                                    backgroundColor: style.bg,
                                    color: style.color,
                                    textTransform: 'capitalize'
                                  }}>
                                    {item.status}
                                  </span>
                                )}
                              </div>
                            </div>
                            {(item.account_name || item.symbol) && (
                              <div style={{ marginTop: '8px', display: 'flex', gap: '12px' }}>
                                {item.account_name && (
                                  <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                    Account: <strong>{item.account_name}</strong>
                                  </span>
                                )}
                                {item.symbol && (
                                  <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                    Symbol: <strong>{item.symbol}</strong>
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {/* Skipped Trades Tab */}
            {activeTab === 'skipped' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Summary Cards */}
                {skippedSummary && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px'
                  }}>
                    <div style={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      padding: '20px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '32px', fontWeight: '700', color: skippedSummary.total_skipped > 0 ? '#f59e0b' : '#10b981' }}>
                        {skippedSummary.total_skipped}
                      </div>
                      <div style={{ fontSize: '14px', color: '#6b7280' }}>Total Skipped (7 days)</div>
                    </div>
                    {Object.entries(skippedSummary.by_reason).map(([reason, count]) => (
                      <div key={reason} style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        padding: '20px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '24px', fontWeight: '600', color: '#374151' }}>{count}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280', textTransform: 'capitalize' }}>
                          {reason.replace(/_/g, ' ')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Skipped Trades Table */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  overflow: 'hidden'
                }}>
                  <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: '#1e293b',
                      margin: 0
                    }}>
                      Skipped Trade Details (Last 7 Days)
                    </h3>
                    <p style={{ fontSize: '14px', color: '#6b7280', margin: '8px 0 0 0' }}>
                      Trade opportunities that were identified but not executed
                    </p>
                  </div>
                  {skippedTrades.length === 0 ? (
                    <div style={{
                      padding: '60px 24px',
                      textAlign: 'center',
                      color: '#10b981'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>✓</div>
                      <p style={{ fontSize: '16px', marginBottom: '8px' }}>No skipped trades</p>
                      <p style={{ fontSize: '14px', color: '#6b7280' }}>All trade opportunities were executed successfully</p>
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f9fafb' }}>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Time</th>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Symbol</th>
                            <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Side</th>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Reason</th>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Details</th>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Account</th>
                            <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Price</th>
                            <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Cash</th>
                          </tr>
                        </thead>
                        <tbody>
                          {skippedTrades.map((skip) => {
                            const reasonColors: Record<string, { bg: string; text: string }> = {
                              no_opportunities: { bg: '#f3f4f6', text: '#6b7280' },
                              auto_trading_disabled: { bg: '#fef3c7', text: '#92400e' },
                              account_inactive: { bg: '#fee2e2', text: '#991b1b' },
                              connection_failed: { bg: '#fee2e2', text: '#991b1b' },
                              no_price: { bg: '#fef3c7', text: '#92400e' },
                              position_too_small: { bg: '#dbeafe', text: '#1e40af' },
                              insufficient_cash: { bg: '#fef3c7', text: '#92400e' },
                              blacklisted: { bg: '#f3f4f6', text: '#374151' },
                              not_in_whitelist: { bg: '#f3f4f6', text: '#374151' },
                            };
                            const colors = reasonColors[skip.reason] || { bg: '#f3f4f6', text: '#374151' };

                            return (
                              <tr key={skip.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                <td style={{ padding: '12px 16px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                                  {formatDateTime(skip.created_at)}
                                </td>
                                <td style={{ padding: '12px 16px', fontWeight: '600', color: '#1e293b' }}>
                                  {skip.symbol || '-'}
                                </td>
                                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                  {skip.side && (
                                    <span style={{
                                      padding: '4px 10px',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      fontWeight: '500',
                                      backgroundColor: skip.side === 'buy' ? '#dcfce7' : '#fee2e2',
                                      color: skip.side === 'buy' ? '#166534' : '#991b1b',
                                      textTransform: 'uppercase'
                                    }}>
                                      {skip.side}
                                    </span>
                                  )}
                                </td>
                                <td style={{ padding: '12px 16px' }}>
                                  <span style={{
                                    padding: '4px 10px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    backgroundColor: colors.bg,
                                    color: colors.text,
                                    textTransform: 'capitalize'
                                  }}>
                                    {skip.reason.replace(/_/g, ' ')}
                                  </span>
                                </td>
                                <td style={{ padding: '12px 16px', color: '#6b7280', maxWidth: '300px' }}>
                                  <div style={{ wordBreak: 'break-word' }}>{skip.reason_detail || '-'}</div>
                                </td>
                                <td style={{ padding: '12px 16px', color: '#374151' }}>
                                  {skip.account_name || '-'}
                                </td>
                                <td style={{ padding: '12px 16px', textAlign: 'right', color: '#374151' }}>
                                  {skip.market_price ? formatCurrency(skip.market_price) : '-'}
                                </td>
                                <td style={{ padding: '12px 16px', textAlign: 'right', color: '#374151' }}>
                                  {skip.available_cash ? formatCurrency(skip.available_cash) : '-'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Strategies Tab */}
            {activeTab === 'strategies' && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                overflow: 'hidden'
              }}>
                <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#1e293b',
                    margin: 0
                  }}>
                    All Strategies
                  </h3>
                </div>
                {strategies.length === 0 ? (
                  <div style={{
                    padding: '60px 24px',
                    textAlign: 'center',
                    color: '#9ca3af'
                  }}>
                    <p style={{ fontSize: '16px' }}>No strategies found</p>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f9fafb' }}>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Strategy</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Account / Owner</th>
                          <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Active</th>
                          <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Auto-Trade</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Trades</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>P&L</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Win Rate</th>
                          <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Parameters</th>
                        </tr>
                      </thead>
                      <tbody>
                        {strategies.map((s) => (
                          <React.Fragment key={s.id}>
                            <tr style={{ borderBottom: editingStrategyId === s.id ? 'none' : '1px solid #f3f4f6' }}>
                              <td style={{ padding: '12px 16px' }}>
                                <div style={{ fontWeight: '600', color: '#1e293b' }}>{s.name}</div>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>{s.strategy_type}</div>
                              </td>
                              <td style={{ padding: '12px 16px' }}>
                                <div style={{ color: '#1e293b' }}>{s.account_name}</div>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>{s.user_name}</div>
                              </td>
                              <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                <button
                                  onClick={() => handleToggleStrategyField(s, 'is_active')}
                                  style={{
                                    position: 'relative',
                                    width: '40px',
                                    height: '22px',
                                    borderRadius: '11px',
                                    border: 'none',
                                    backgroundColor: s.is_active ? '#10b981' : '#d1d5db',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s'
                                  }}
                                  title={s.is_active ? 'Active - click to deactivate' : 'Inactive - click to activate'}
                                >
                                  <span style={{
                                    position: 'absolute',
                                    top: '2px',
                                    left: s.is_active ? '20px' : '2px',
                                    width: '18px',
                                    height: '18px',
                                    borderRadius: '50%',
                                    backgroundColor: 'white',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                    transition: 'left 0.2s'
                                  }} />
                                </button>
                              </td>
                              <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                <button
                                  onClick={() => handleToggleStrategyField(s, 'is_automated')}
                                  style={{
                                    position: 'relative',
                                    width: '40px',
                                    height: '22px',
                                    borderRadius: '11px',
                                    border: 'none',
                                    backgroundColor: s.is_automated ? '#10b981' : '#d1d5db',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s'
                                  }}
                                  title={s.is_automated ? 'Auto-trading on - click to disable' : 'Auto-trading off - click to enable'}
                                >
                                  <span style={{
                                    position: 'absolute',
                                    top: '2px',
                                    left: s.is_automated ? '20px' : '2px',
                                    width: '18px',
                                    height: '18px',
                                    borderRadius: '50%',
                                    backgroundColor: 'white',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                    transition: 'left 0.2s'
                                  }} />
                                </button>
                              </td>
                              <td style={{ padding: '12px 16px', textAlign: 'right', color: '#374151' }}>
                                {s.total_trades}
                              </td>
                              <td style={{
                                padding: '12px 16px',
                                textAlign: 'right',
                                fontWeight: '500',
                                color: s.total_pnl >= 0 ? '#10b981' : '#ef4444'
                              }}>
                                {formatCurrency(s.total_pnl)}
                              </td>
                              <td style={{ padding: '12px 16px', textAlign: 'right', color: '#374151' }}>
                                {s.total_trades > 0 ? formatPercent(s.win_rate, 0) : '-'}
                              </td>
                              <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                {editingStrategyId === s.id ? (
                                  <button
                                    onClick={handleCancelEdit}
                                    style={{
                                      padding: '6px 14px',
                                      backgroundColor: '#f3f4f6',
                                      color: '#374151',
                                      border: 'none',
                                      borderRadius: '4px',
                                      fontSize: '13px',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    Cancel
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleEditStrategy(s)}
                                    style={{
                                      padding: '6px 14px',
                                      backgroundColor: '#3b82f6',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      fontSize: '13px',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    Edit
                                  </button>
                                )}
                                {strategyMessage && strategyMessage.id === s.id && editingStrategyId !== s.id && (
                                  <div style={{
                                    fontSize: '12px',
                                    marginTop: '4px',
                                    color: strategyMessage.type === 'success' ? '#10b981' : '#ef4444'
                                  }}>
                                    {strategyMessage.text}
                                  </div>
                                )}
                              </td>
                            </tr>
                            {/* Inline parameter editor */}
                            {editingStrategyId === s.id && (
                              <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                                <td colSpan={8} style={{ padding: '0 16px 16px 16px' }}>
                                  <div style={{
                                    backgroundColor: '#f9fafb',
                                    borderRadius: '6px',
                                    padding: '16px 20px',
                                    display: 'flex',
                                    gap: '20px',
                                    alignItems: 'flex-end',
                                    flexWrap: 'wrap'
                                  }}>
                                    <div>
                                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                                        Max Position Value ($)
                                      </label>
                                      <input
                                        type="number"
                                        step="100"
                                        min="0"
                                        value={editForm.max_position_value}
                                        onChange={e => setEditForm(f => ({ ...f, max_position_value: e.target.value }))}
                                        style={{
                                          padding: '8px 12px',
                                          border: '1px solid #d1d5db',
                                          borderRadius: '6px',
                                          fontSize: '14px',
                                          width: '140px'
                                        }}
                                        placeholder="e.g. 10000"
                                      />
                                    </div>
                                    <div>
                                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                                        Max Position (%)
                                      </label>
                                      <input
                                        type="number"
                                        step="1"
                                        min="0"
                                        max="100"
                                        value={editForm.max_position_pct}
                                        onChange={e => setEditForm(f => ({ ...f, max_position_pct: e.target.value }))}
                                        style={{
                                          padding: '8px 12px',
                                          border: '1px solid #d1d5db',
                                          borderRadius: '6px',
                                          fontSize: '14px',
                                          width: '100px'
                                        }}
                                        placeholder="e.g. 5"
                                      />
                                    </div>
                                    <div>
                                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                                        Max Portfolio Allocation (%)
                                      </label>
                                      <input
                                        type="number"
                                        step="1"
                                        min="0"
                                        max="100"
                                        value={editForm.max_portfolio_allocation_pct}
                                        onChange={e => setEditForm(f => ({ ...f, max_portfolio_allocation_pct: e.target.value }))}
                                        style={{
                                          padding: '8px 12px',
                                          border: '1px solid #d1d5db',
                                          borderRadius: '6px',
                                          fontSize: '14px',
                                          width: '100px'
                                        }}
                                        placeholder="e.g. 50"
                                      />
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                      <button
                                        onClick={() => handleSaveStrategy(s)}
                                        disabled={savingStrategy}
                                        style={{
                                          padding: '8px 20px',
                                          backgroundColor: savingStrategy ? '#9ca3af' : '#10b981',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '6px',
                                          fontSize: '14px',
                                          fontWeight: '500',
                                          cursor: savingStrategy ? 'not-allowed' : 'pointer'
                                        }}
                                      >
                                        {savingStrategy ? 'Saving...' : 'Save'}
                                      </button>
                                      <button
                                        onClick={handleCancelEdit}
                                        style={{
                                          padding: '8px 20px',
                                          backgroundColor: '#f3f4f6',
                                          color: '#374151',
                                          border: 'none',
                                          borderRadius: '6px',
                                          fontSize: '14px',
                                          fontWeight: '500',
                                          cursor: 'pointer'
                                        }}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                    {strategyMessage && strategyMessage.id === s.id && (
                                      <div style={{
                                        fontSize: '13px',
                                        color: strategyMessage.type === 'success' ? '#10b981' : '#ef4444',
                                        alignSelf: 'center'
                                      }}>
                                        {strategyMessage.text}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default Admin;
